'use client';
import { useCallback, useState } from 'react';

import { NSFWRoutesRedirects } from '~constants/routes';
import { ConfirmModal, NSFWAgeConfirmation } from '~features/nsfw';
import { useModal } from '~hooks/useModal';
import { useSession } from '~hooks/useSession';
import { usePathname, useRouter } from '~i18n/navigation';
import { GTMAnalytics } from '~services/analytics';

import { AgeConfirmationContext } from './index';
import type { TAgeConfirmationConfig } from './types';

type Props = {
	children: React.ReactNode;
};

export const AgeConfirmationProvider = ({ children }: Props) => {
	const { isOpen, open, close } = useModal();
	const [showCancelButton, setShowCancelButton] = useState(true);
	const { isDemoUser } = useSession();

	const pathname = usePathname();
	const router = useRouter();

	const openAgeConfirmation = useCallback(
		(config: TAgeConfirmationConfig) => {
			open({ cb: config.callback })();
		},
		[open]
	);

	const handleConfirm = useCallback(() => {
		setShowCancelButton(true);
		close(true);
		if (isDemoUser) {
			GTMAnalytics.ageConfirmed();
		}
	}, [close, isDemoUser]);

	const handleCancel = useCallback(() => {
		setShowCancelButton(true);
		close(false);
	}, [close]);

	const text =
		"This page may contain sensitive or adult content. To continue, confirm you’re over 18 years old and we'll update your settings to show mature content." +
		'\n\n' +
		'You can hide mature content again at any time in your settings.';

	return (
		<AgeConfirmationContext.Provider value={{ openAgeConfirmation }}>
			{children}

			<NSFWAgeConfirmation />

			<ConfirmModal
				title="Mature content"
				info={text}
				locked={true}
				hasCancel={showCancelButton}
				onConfirm={handleConfirm}
				onClose={handleCancel}
				onCancel={() => {
					if (NSFWRoutesRedirects[pathname]) {
						router.push(NSFWRoutesRedirects[pathname]);
					}
					handleCancel();
				}}
				open={isOpen}
				confirmText="Yes, I’m over 18"
				cancelText="Go back"
				align="left"
				transparent={false}
				multiline={true}
				hasClose={false}
			/>
		</AgeConfirmationContext.Provider>
	);
};
